import { KX_ROUTER_ADDRESSES } from 'constants/addresses'
import { calculateGasMargin } from 'utils/calculateGasMargin'
import type { Hex, PublicClient, SendTransactionReturnType } from 'viem'
import { Address } from 'viem'

import { DoubleSidedParams, NativeParams, SingleSidedParams, VaultStrategy, WithdrawParams } from './VaultStrategy.type'

/**
 * KXRouterStrategy - A unified strategy that uses KX Router for single-sided zaps
 * while delegating double-sided deposits to existing strategies
 */
export class KXRouterStrategy implements VaultStrategy {
  readonly supportsZapping = true
  readonly supportsAutoCompound = true
  kxRouterAddress: string

  constructor(
    private existingStrategy: VaultStrategy,
    private poolType: 'island' | 'charmvault' | 'v2',
    private useSingleSided: boolean,
    private wagmiPublicClient: PublicClient,
    private sendTransactionAsync: (args: {
      to: Address
      data: Hex
      value?: bigint
      gas?: bigint
      account: Address
    }) => Promise<SendTransactionReturnType>,
    private chainId: number
  ) {
    this.kxRouterAddress = KX_ROUTER_ADDRESSES[chainId]
  }

  get routerAddress(): string {
    // For single-sided, we'll use the KX Router address from the quote
    // For double-sided, delegate to existing strategy
    if (this.useSingleSided) {
      return this.kxRouterAddress
    }
    return this.existingStrategy.routerAddress
  }

  // ========================================
  // PRIVATE METHODS
  // ========================================
  private handleKXRouterError(error: any, context: string): never {
    console.error(`KX Router ${context} failed:`, error)

    // Parse error message
    const errorMessage = error?.message || error?.toString() || 'Transaction failed'
    const errorMessageLower = errorMessage.toLowerCase()
    // Check for specific error cases
    if (errorMessageLower.includes('below min amounts')) {
      throw new Error('Output amount is below minimum. Please increase slippage tolerance and try again.')
    }

    // Check for insufficient funds
    if (errorMessageLower.includes('insufficient funds') || errorMessageLower.includes('insufficient balance')) {
      throw new Error('Insufficient balance to complete this transaction.')
    }
    // Check for general execution reverted
    if (errorMessageLower.includes('execution reverted')) {
      throw new Error('Try again and if error persists use double sided.')
    }
    throw error
  }

  // ========================================
  // DOUBLE-SIDED LIQUIDITY - Delegate to existing strategy
  // ========================================
  async addLiquidityDoubleSided(params: DoubleSidedParams): Promise<Hex> {
    // We assume the existing strategy has been fixed to return Hex
    return this.existingStrategy.addLiquidityDoubleSided(params)
  }

  // ========================================
  // SINGLE-SIDED LIQUIDITY - Use KX Router
  // ========================================
  async addLiquiditySingleSided(params: SingleSidedParams & { kxRouterQuote?: any }): Promise<Hex> {
    const { account, kxRouterQuote } = params
    if (!kxRouterQuote || !kxRouterQuote.methodParameters) {
      throw new Error('KX Router quote required for single-sided deposits')
    }

    const { methodParameters } = kxRouterQuote

    try {
      // Prepare transaction using KX Router quote
      const tx = {
        to: methodParameters.to as Address,
        data: methodParameters.calldata as Hex,
        value: BigInt(methodParameters.value || '0'),
        account: account as Address,
      } as const

      // Estimate gas
      const estimatedGas = await this.wagmiPublicClient.estimateGas(tx)

      // Execute transaction
      const txHash = await this.sendTransactionAsync({
        ...tx,
        gas: calculateGasMargin(estimatedGas) as bigint,
      })

      return txHash
    } catch (error: any) {
      return this.handleKXRouterError(error, 'single-sided deposit')
    }
  }

  // ========================================
  // NATIVE TOKEN LIQUIDITY - Use KX Router
  // ========================================
  async addLiquidityNative(params: NativeParams & { kxRouterQuote?: any }): Promise<Hex> {
    const { account, amount, kxRouterQuote } = params

    if (!kxRouterQuote || !kxRouterQuote.methodParameters) {
      throw new Error('KX Router quote required for native token deposits')
    }

    const { methodParameters } = kxRouterQuote

    try {
      // Prepare transaction using KX Router quote
      const tx = {
        to: methodParameters.to as Address,
        data: methodParameters.calldata as Hex,
        value: BigInt(methodParameters.value || amount),
        account: account as Address,
      } as const

      // Estimate gas
      const estimatedGas = await this.wagmiPublicClient.estimateGas(tx)

      // Execute transaction
      const txHash = await this.sendTransactionAsync({
        ...tx,
        gas: calculateGasMargin(estimatedGas) as bigint,
      })

      return txHash
    } catch (error: any) {
      return this.handleKXRouterError(error, 'native deposit')
    }
  }

  // ========================================
  // WITHDRAW LIQUIDITY - Delegate to existing strategy
  // ========================================
  async withdrawLiquidity(params: WithdrawParams): Promise<Hex> {
    return this.existingStrategy.withdrawLiquidity(params)
  }

  // ========================================
  // STAKING METHODS - Delegate if supported
  // ========================================
  stakeLiquidity?(params: any): Promise<Hex> {
    if (this.existingStrategy.stakeLiquidity) {
      return this.existingStrategy.stakeLiquidity(params)
    }
    throw new Error('Staking not supported')
  }

  unstakeLiquidity?(params: any): Promise<Hex> {
    if (this.existingStrategy.unstakeLiquidity) {
      return this.existingStrategy.unstakeLiquidity(params)
    }
    throw new Error('Unstaking not supported')
  }
}
